import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  AppBar,
  IconButton,
  Button,
  Chip,
  Divider,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Dashboard,
  Description,
  Category,
  LocalOffer,
  AttachMoney,
  Warning,
  Discount,
  ViewModule,
  ArrowBack,
  Extension,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks';
import { useUnreadCount } from '../../hooks';
import { colors } from '../../theme/theme';

const DRAWER_WIDTH = 280;

interface ModuleLayoutProps {
  children: ReactNode;
  module?: any;
  moduleId: string | number;
}

export const ModuleLayout = ({ children, module, moduleId }: ModuleLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { count: unreadCount } = useUnreadCount();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    {
      title: 'Dashboard',
      icon: <Dashboard />,
      path: `/modules/${moduleId}`,
      description: 'Overview & Analytics',
    },
    {
      title: 'Schedules',
      icon: <Description />,
      path: `/modules/${moduleId}/schedules`,
      description: 'Manage schedules',
    },
    {
      title: 'Parts',
      icon: <Extension />,
      path: `/modules/${moduleId}/parts`,
      description: 'Module parts',
    },
    {
      title: 'Categories',
      icon: <Category />,
      path: `/modules/${moduleId}/categories`,
      description: 'Service categories',
    },
    {
      title: 'Services',
      icon: <LocalOffer />,
      path: `/modules/${moduleId}/services`,
      description: 'Available services',
    },
    {
      title: 'Charges',
      icon: <AttachMoney />,
      path: `/modules/${moduleId}/charges`,
      description: 'Pricing & fees',
    },
    {
      title: 'Penalties',
      icon: <Warning />,
      path: `/modules/${moduleId}/penalties`,
      description: 'Penalty rules',
    },
    {
      title: 'Discounts',
      icon: <Discount />,
      path: `/modules/${moduleId}/discounts`,
      description: 'Discount & waivers',
    },
  ];

  const isActive = (path: string) => {
    if (path === `/modules/${moduleId}`) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: colors.grey[50] }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)',
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
          boxShadow: `0 2px 8px ${colors.primary.main}14`,
          borderBottom: `1px solid ${colors.primary.main}14`,
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          {/* Page Title */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                fontSize: '1.5rem',
                letterSpacing: '-0.5px',
              }}
            >
              {menuItems.find(m => m.path === `/modules/${moduleId}`)?.title ||
               menuItems.find(m => m.path === location.pathname)?.title ||
               module?.name || 'Module'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontSize: '0.8125rem',
                fontWeight: 500,
              }}
            >
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/notifications')}
              sx={{
                bgcolor: `${colors.primary.main}14`,
                width: 44,
                height: 44,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: `${colors.primary.main}26`,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#ef4444',
                    color: 'white',
                    fontWeight: 700,
                  },
                }}
              >
                <NotificationsIcon sx={{ color: colors.primary.main }} />
              </Badge>
            </IconButton>

            {/* Profile Menu */}
            <IconButton
              edge="end"
              onClick={handleProfileMenuOpen}
              sx={{
                bgcolor: `${colors.primary.main}14`,
                width: 44,
                height: 44,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: `${colors.primary.main}26`,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: colors.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.name}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Module Sidebar */}
      <Drawer
        variant="permanent"
        open
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${colors.grey[200]}`,
            bgcolor: '#ffffff',
          },
        }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
        }}
      >
          {/* Header Section */}
          <Box
            sx={{
              p: 3,
              pb: 2.5,
              background: colors.gradients.primary,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
              flexShrink: 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -30,
                right: -30,
                width: 120,
                height: 120,
                opacity: 0.15,
                background: 'white',
                borderRadius: '50%',
              },
            }}
          >
            {/* K-Pay Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2.5,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  color: 'white',
                }}
              >
                K
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1,
                    fontSize: '1.75rem',
                    letterSpacing: '-2px',
                    color: 'white',
                  }}
                >
                  K-Pay
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Revenue Platform
                </Typography>
              </Box>
            </Box>

            {/* Module Card */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                p: 2,
                borderRadius: 2.5,
                position: 'relative',
                zIndex: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ViewModule sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    Module
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      lineHeight: 1.2,
                      fontSize: '1rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {module?.name || 'Loading...'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Back Button */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/modules')}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                position: 'relative',
                zIndex: 1,
                py: 1,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Back to Modules
            </Button>
          </Box>

          <Divider sx={{ borderColor: colors.grey[200] }} />

          {/* Navigation Menu */}
          <List sx={{
            px: 2.5,
            py: 2.5,
            flexGrow: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: colors.grey[300],
              borderRadius: '10px',
              '&:hover': {
                bgcolor: colors.grey[400],
              },
            },
          }}>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '&.Mui-selected': {
                      bgcolor: colors.primary.main,
                      color: 'white',
                      boxShadow: colors.shadows.button,
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                      '&:hover': {
                        bgcolor: colors.primary.dark,
                        transform: 'translateX(4px)',
                      },
                    },
                    '&:hover': {
                      bgcolor: colors.primary[50],
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path) ? 'white' : colors.primary.main,
                      minWidth: 40,
                      transition: 'color 200ms',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    secondary={!isActive(item.path) ? item.description : undefined}
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path) ? 700 : 600,
                      fontSize: '0.9375rem',
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      sx: { color: isActive(item.path) ? 'rgba(255,255,255,0.8)' : colors.grey[600] },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Module Info */}
          {module && (
            <Box sx={{
              mt: 'auto',
              p: 2.5,
              bgcolor: colors.grey[50],
              borderTop: `1px solid ${colors.grey[200]}`,
              flexShrink: 0,
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.grey[600],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.6875rem',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Module Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={module.status || 'active'}
                  color={module.status === 'active' ? 'success' : 'default'}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: 1.5,
                  }}
                />
                {module.is_billable && (
                  <Chip
                    label="Billable"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      bgcolor: colors.primary[50],
                      color: colors.primary.main,
                    }}
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: '64px',
          minHeight: '100vh',
          bgcolor: colors.grey[50],
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
